import { Check, ChevronDown } from 'lucide-react';
import { confirmedSettlementAt, isInPersonTransaction, isProviderFundingConfirmed } from './transactionState';
import { journeyStepTimes } from './paymentRecord';

export function journeyPresentation(transaction, uiState, { role, paymentStillSettling = false } = {}) {
  const funded = isProviderFundingConfirmed(transaction);
  // Honor the existing resolver, including its certified legacy/direct-accept release handling.
  const released = ['RELEASED', 'PAYOUT_REVIEW', 'COMPLETED'].includes(uiState.state);
  // Claim bank settlement only when it is actually confirmed (same evidence as the payout timeline).
  const settled = uiState.state === 'COMPLETED' && Boolean(confirmedSettlementAt(transaction));
  const terminalProtection = { REFUNDED: 'Payment Refunded', CANCELLED: 'Transaction Cancelled', EXPIRED: 'Transaction Expired' }[uiState.state];
  const awaitingPayment = uiState.state === 'FUNDED' && uiState.presentationState === 'AWAITING_PAYMENT' && uiState.actionable === true && !funded;
  const knownState = ['CREATED', 'FUNDED', 'ESCROW_LOCKED', 'DELIVERY_PENDING', 'DELIVERED', 'RELEASED', 'COMPLETED', 'DISPUTED', 'EXPIRED', 'REFUNDED', 'CANCELLED', 'PAYOUT_REVIEW'].includes(uiState.state);
  // Display-only guard: an unsupported resolver result must not invent a financial action.
  const unsupported = !knownState || (uiState.state === 'FUNDED' && !awaitingPayment) || (!funded && !released && !terminalProtection && !['CREATED', 'DISPUTED'].includes(uiState.state) && !awaitingPayment);
  const stopped = unsupported || ['DISPUTED', 'EXPIRED', 'REFUNDED', 'CANCELLED', 'PAYOUT_REVIEW'].includes(uiState.state);
  const receiptConfirmed = transaction.delivery_confirmed === true || Boolean(transaction.buyer_confirmed_receipt_at);
  let index = { CREATED: 0, FUNDED: 1, ESCROW_LOCKED: 3, DELIVERY_PENDING: 3, DELIVERED: receiptConfirmed ? 5 : 4, RELEASED: 6, COMPLETED: 7 }[uiState.state] ?? 0;
  // Presentation may never advance the payment/release markers without the existing authoritative evidence.
  if (unsupported) index = 0;
  if (released) index = settled ? 7 : 6;
  const handoverInProgress = uiState.state === 'DELIVERY_PENDING';
  const owner = stopped ? 'Support / transaction participants' : index === 0 ? (!transaction.buyer_confirmed && !transaction.seller_confirmed ? 'Buyer and seller' : !transaction.buyer_confirmed ? 'Buyer' : 'Seller') : index === 1 ? 'Buyer' : index === 3 ? (paymentStillSettling ? 'Payment provider' : handoverInProgress ? 'Buyer and seller' : 'Seller') : index === 4 ? 'Buyer' : index === 5 || index === 6 ? 'Payment provider' : 'No further action';
  let next = unsupported ? 'Check the current transaction status and contact support before taking further action.' : stopped ? uiState.description : index === 0 ? 'Review and confirm the agreed transaction details.' : awaitingPayment ? 'Complete payment through this transaction’s secure checkout.' : index === 3 ? (paymentStillSettling ? 'Wait for the provider to finish confirming payment before delivery.' : handoverInProgress ? 'Complete the agreed handover. The buyer confirms receipt after receiving and checking the item or service.' : 'Follow the agreed delivery or handover process.') : index === 4 ? 'Receive and check the item or service, then confirm receipt.' : index === 5 ? 'Receipt is confirmed. Await provider confirmation of payment release.' : index === 6 ? 'Payment released. Bank settlement is pending confirmation.' : 'Payment release and seller bank settlement are confirmed.';
  if (!unsupported && !stopped) {
    const inPerson = isInPersonTransaction(transaction);
    if (role === 'seller') {
      if (awaitingPayment || index === 1) next = 'Waiting for buyer payment.';
      else if (inPerson && index === 3 && !paymentStillSettling && !handoverInProgress) next = 'Arrange the in-person handover.';
      else if (index === 6) next = 'Payment released — payout processing.';
    } else if (role === 'buyer') {
      if (awaitingPayment) next = 'Complete secure payment.';
      else if (inPerson && index === 4) next = 'Confirm handover to release payment.';
    }
  }
  const safety = stopped ? 'Check the current status and contact support if you need help.' : released ? 'Payment release and bank settlement are separate steps.' : !funded ? (role === 'seller' ? 'Do not hand over the item until this transaction shows Payment Protected.' : 'Only complete payment through the secure checkout opened from this transaction.') : index === 4 ? 'Only confirm receipt once you have received and checked the agreed item or service.' : 'Payment has been confirmed as protected. Follow the agreed delivery process.';
  const awaitingSellerAcceptance = !stopped && index === 0 && !funded && !released && !transaction.seller_confirmed;
  if (awaitingSellerAcceptance) next = role === 'seller'
    ? 'Review and accept the transaction details first. Secure payment opens after both parties confirm.'
    : 'Waiting for seller acceptance. Secure payment opens after both parties confirm.';
  return { index, stopped, owner, next, safety, protection: terminalProtection || (unsupported ? 'Status needs review' : released ? 'Payment Released' : funded ? 'Payment Protected' : awaitingSellerAcceptance ? 'Awaiting seller acceptance' : 'Payment not yet confirmed') };
}

// Premium 4-phase presentation, built entirely on top of journeyPresentation's
// existing index/stopped/owner/next/protection — no new transaction states, no
// change to the underlying 8-step resolver above. The 8 granular states group
// into 4 customer-facing phases as follows (index -> phase):
//   0                  -> Agreement
//   1, 2               -> Payment            (2 = "Payment Protected", reached
//                          retroactively the instant funding completes — see
//                          the paymentProtected sub-badge below, which is tied
//                          to the actual funding evidence, not to index === 2
//                          ever being "current", since no state maps there)
//   3, 4                -> Handover            (Delivery/Handover, Buyer Confirmation)
//   5, 6, 7             -> Release             (Payment Released, Seller Payout
//                          Processing, Complete)
// The 4th phase is named "Release", not "Complete" — Payment Released and
// Seller Payout Processing both sit inside it, and showing "Complete" as
// the active/current phase label while release is still being confirmed or
// payout is still processing would misleadingly claim the transaction is
// finished before bank settlement is actually confirmed. Only once index=7
// (settled === true in journeyPresentation, uiState.state === 'COMPLETED')
// does the UI switch to the distinct "Transaction Complete" success
// treatment below — see trulyComplete in TransactionJourney.
const JOURNEY_STEP_LABELS = ['Agreement', 'Payment', 'Payment Protected', 'Delivery / Handover', 'Buyer Confirmation', 'Payment Released', 'Seller Payout Processing', 'Complete'];
const JOURNEY_PHASES = [
  { key: 'agreement', label: 'Agreement', indices: [0] },
  { key: 'payment', label: 'Payment', indices: [1, 2] },
  { key: 'handover', label: 'Handover', indices: [3, 4] },
  { key: 'release', label: 'Release', indices: [5, 6, 7] },
];

function journeyPhaseIndex(stepIndex) {
  const found = JOURNEY_PHASES.findIndex((phase) => phase.indices.includes(stepIndex));
  return found === -1 ? 0 : found;
}

export default function TransactionJourney({ transaction, uiState, role, paymentStillSettling }) {
  const view = journeyPresentation(transaction, uiState, { role, paymentStillSettling });
  const currentPhase = journeyPhaseIndex(view.index);
  // Tied to the actual funding evidence (same resolver the rest of the app
  // uses for "Payment Protected"), never to the journey index — see the
  // comment above JOURNEY_PHASES for why index alone can't drive this badge.
  const paymentProtected = isProviderFundingConfirmed(transaction);
  // Only true once the existing resolver has ACTUALLY reached the final
  // index (7 = settled, uiState.state === 'COMPLETED') — never while release
  // is still being confirmed or payout is still processing (index 5/6,
  // still inside the "Release" phase). Drives the distinct success
  // treatment below instead of a merely "current" Release phase.
  const trulyComplete = !view.stopped && view.index === 7;

  // Continuous vertical timeline: a real connector element between each
  // step (not a pseudo-element), filled solid up to the current step so the
  // line itself reads as "how far along" at a glance.
  const stepTimes = journeyStepTimes(transaction);
  const detailSteps = JOURNEY_STEP_LABELS.map((label, index) => {
    const stateClass = view.stopped ? (index < view.index ? 'is-done' : 'is-upcoming') : index < view.index ? 'is-done' : index === view.index ? 'is-current' : 'is-upcoming';
    return (
      <li key={label}>
        {index > 0 && <span className={`tt-journey-detail-connector ${index <= view.index && !view.stopped ? 'is-filled' : ''}`} aria-hidden="true" />}
        <div aria-current={!view.stopped && index === view.index ? 'step' : undefined} className={stateClass}>
          <span className="tt-journey-detail-dot">{!view.stopped && index < view.index ? <Check size={13} aria-label="Complete" /> : null}</span>
          <strong>{label}</strong>
          {index < view.index && !view.stopped && stepTimes[index] ? <time className="tt-journey-detail-time" data-testid={`journey-time-${index}`}>{stepTimes[index]}</time> : (index === 7 && trulyComplete && stepTimes[7] ? <time className="tt-journey-detail-time" data-testid="journey-time-7">{stepTimes[7]}</time> : null)}
        </div>
      </li>
    );
  });

  const phaseRail = JOURNEY_PHASES.map((phase, phaseIdx) => {
    // Once trulyComplete, the Release phase itself is "done" (green), not
    // "current" (blue) — there is no longer an active/in-progress phase.
    const status = phaseIdx < currentPhase ? 'done' : phaseIdx === currentPhase ? (trulyComplete ? 'done' : view.stopped ? 'attention' : 'current') : 'upcoming';
    const captionByStatus = { done: 'Completed', current: 'Current', attention: 'Needs attention', upcoming: 'Upcoming' };
    // Post-release, pre-settlement (index 6: a real provider release has
    // already been confirmed, bank settlement is not) must not read as
    // "Release: Current" — that implies release itself is still pending.
    // Index 5 (receipt confirmed, release not yet confirmed) keeps the
    // normal "Release" label since release genuinely is the pending action
    // there. See TT-296674 (2026-10-13).
    const label = phase.key === 'release' && status === 'current' && view.index === 6 ? 'Payout' : phase.label;
    return (
      <div key={phase.key} className="tt-phase-step">
        {phaseIdx > 0 && <span className={`tt-phase-connector ${phaseIdx <= currentPhase ? 'is-filled' : ''}`} aria-hidden="true" />}
        <div className={`tt-phase tt-phase-${status}`} aria-current={status === 'current' ? 'step' : undefined}>
          <span className="tt-phase-icon" aria-hidden="true">{status === 'done' ? <Check size={14} /> : <span className="tt-phase-dot" />}</span>
          <span className="tt-phase-text">
            <span className="tt-phase-label">{label}</span>
            <span className="tt-phase-caption">{captionByStatus[status]}</span>
            {phase.key === 'payment' && paymentProtected && (
              <span className="tt-phase-subbadge"><Check size={11} aria-hidden="true" /> Payment Protected</span>
            )}
          </span>
        </div>
      </div>
    );
  });

  return (
    <section className="tt-journey tt-journey-quiet" aria-label="Transaction journey">
      {/* No per-phase status copy here, and no top-right status badge — the
          hero (TransactionStateSummary, rendered above this section on the
          page) already names the current state, the required action, and
          the safety note once. Duplicating that text here would repeat the
          same information in a second panel. The rail alone communicates
          "where am I" (done/current/upcoming); the one exception is the
          distinct success treatment below, which only appears once the
          transaction has ACTUALLY settled (trulyComplete) — the hero's own
          "Transaction complete" title lacks this section's strong visual
          confirmation, so it earns its place here. */}
      <div className="tt-journey-heading">
        <h2>Transaction progress</h2>
      </div>
      <div className="tt-phase-rail" role="list" aria-label="Transaction phases">{phaseRail}</div>
      {trulyComplete && (
        <div className="tt-phase-detail tt-phase-detail-success" data-testid="journey-complete-banner">
          <span className="tt-phase-detail-success-badge"><Check size={15} aria-hidden="true" /> Transaction Complete</span>
          <p className="tt-phase-detail-body">{view.next}</p>
        </div>
      )}
      <details className="tt-journey-detail-toggle">
        <summary>View full transaction journey <ChevronDown size={16} aria-hidden="true" /></summary>
        <ol className="tt-journey-detail-steps">{detailSteps}</ol>
      </details>
    </section>
  );
}
